import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { SharedChipProps } from '../../types/SharedChipProps';
import { StatusType } from '../../types/StatusType';
import { TextColor } from '../../types/TextColor';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { LoadingShimmer } from '../LoadingShimmer/LoadingShimmer';
import { MarkedText } from '../MarkedText/MarkedText';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Tooltip } from '../Tooltip/Tooltip';
import { usePreset } from './usePreset';

const STATUS_TEXT_COLOR: Record<StatusType | 'clear', TextColor> = {
  active: 'status.active',
  ai: 'text.primary',
  danger: 'status.danger',
  alert: 'status.alert',
  inactive: 'text.secondary',
  info: 'text.secondary',
  new: 'status.active',
  success: 'status.success',
  warning: 'status.warning.text',
  clear: 'text.quiet',
};

export const Chip = memo((props: SharedChipProps) => {
  const intl = useIntl();
  const styles = useStyles();

  const {
    textColor,
    disabled,
    icon: propIcon,
    isLoading,
    mode,
    preset,
    status: propStatus,
    text: propText,
    tooltip,
    width = 'min-content',
    search,
    onRemove,
    onClick,
  } = props;

  const presetResult = usePreset(preset);

  const icon = presetResult.icon ?? propIcon;
  const status = presetResult.status ?? propStatus ?? 'info';
  const text = presetResult.text ?? propText;

  const isTagMode = mode === 'tag';

  const border = isTagMode ? undefined : 'border';
  const borderRadius = isTagMode ? 'm' : 'pill';

  const textContent = (
    <TruncateText
      flex={onClick ? 1 : undefined}
      minWidth={0}
      variant={textColor === 'status' ? 'tiny-bold' : 'tiny'}
      whiteSpace="nowrap"
      color={textColor === 'status' ? STATUS_TEXT_COLOR[status] : undefined}
    >
      <MarkedText text={text} search={search ?? ''} />
    </TruncateText>
  );

  const content = isLoading ? (
    <LoadingShimmer height="calc(100% - 8px)" width="100%" />
  ) : (
    <>
      {onClick && !disabled ? (
        <Layout
          as="button"
          type="button"
          align="center"
          flex={1}
          minWidth={0}
          overflow="hidden"
          data-eds-styles-disable-interactable
          styles={componentStyles.clickable}
          onClick={() => onClick(props)}
        >
          {icon && <Icon icon={icon} ml={-1} />}
          {textContent}
        </Layout>
      ) : (
        <>
          {icon && <Icon icon={icon} ml={-1} />}
          {textContent}
        </>
      )}

      {onRemove && (
        <Box mr={-1}>
          <IconButton
            icon="x"
            size="s"
            tooltip={intl.formatMessage({
              id: 'Chip.remove',
              defaultMessage: 'Remove',
              description: 'Tooltip for removing a chip',
            })}
            onClick={onRemove}
          />
        </Box>
      )}
    </>
  );
  return (
    <Tooltip tooltip={tooltip}>
      <Layout
        role="listitem"
        align="center"
        border={border}
        borderRadius={borderRadius}
        disabled={disabled}
        spacing={1}
        styles={[
          componentStyles.base,
          styles.colors.status,
          ...(onClick && !disabled ? [componentStyles.raiseOnHover] : []),
        ]}
        styleProps={{
          boxSizing: onRemove ? 'content-box' : 'border-box',
          status,
          width,
        }}
      >
        {content}
      </Layout>
    </Tooltip>
  );
});

export const componentStyles = {
  base: ({
    boxSizing,
    width,
  }: {
    boxSizing: 'border-box' | 'content-box';
    width: string;
  }) => ({
    border: 'none',
    boxSizing,
    height: 'height.s',
    maxWidth:
      boxSizing === 'content-box' ? 'chip.max-width' : 'chip.auto.max-width',
    outline: 'none',
    paddingLeft: 3,
    paddingRight: 3,
    width,
  }),
  clickable: {
    background: 'none',
    border: 'none',
    borderRadius: 'pill',
    boxShadow: 'none',
    cursor: 'pointer',
    font: 'inherit',
    minWidth: 0,
    padding: 0,
  },
  raiseOnHover: {
    '&:hover': {
      boxShadow: 'raised',
    },
  },
};
